import * as React from 'react';

import { InfoTooltip } from '../../../tooltip';

export type AutoFillValueMetadata = {
  isAutoFilled: boolean;
  autoFilledAt?: string;
  authorId?: string;
};

type AutoFilledIndicatorProps = {
  valueMetadata?: AutoFillValueMetadata;
};

/**
 * Provenance indicator (the blue `*`) shown next to a token whose value was auto-filled from
 * FHIR data during server-side hydration. Renders nothing unless `valueMetadata.isAutoFilled`
 * is set; the tooltip surfaces when it was filled and by which actor. Pairs with
 * `ChangedValueIndicator` (the `†`), which marks a value a clinician later edited.
 */
export const AutoFilledIndicator: React.FC<AutoFilledIndicatorProps> = ({
  valueMetadata,
}) => {
  if (!valueMetadata?.isAutoFilled) {
    return null;
  }

  const description = buildAutoFilledDescription(valueMetadata);

  return (
    <InfoTooltip title="Auto-filled" description={description} side="top">
      <span
        aria-label="Auto-filled value"
        className="ml-0.5 inline-flex cursor-default text-xs font-bold text-blue-500"
        contentEditable={false}
      >
        *
      </span>
    </InfoTooltip>
  );
};

function buildAutoFilledDescription(
  valueMetadata: AutoFillValueMetadata,
): string | undefined {
  const details: string[] = [];

  if (valueMetadata.autoFilledAt) {
    details.push(`Filled at: ${valueMetadata.autoFilledAt}`);
  }

  if (valueMetadata.authorId) {
    details.push(`Author: ${valueMetadata.authorId}`);
  }

  return details.length > 0 ? details.join(' • ') : undefined;
}
